import React, { useEffect, useState } from 'react'
import Header from './Header.jsx'
import Navigator from './Navigator.jsx'
import { Link } from 'react-router-dom'
import { useParams } from 'react-router-dom';
import EventCard from './EventCard.jsx';
import "../css/EventDetails.css"
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import Likes from './Likes.jsx';
import Footer from './Footer.jsx';
import { motion, AnimatePresence } from "framer-motion"
import EventDetailsSlider from './EventDetailsSlider.jsx';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Calendar, MapPin, Clock, TicketSlash, UserCog, Share2, Heart, Eye, Star, ArrowRight, Phone, Mail, Globe, Ticket, Info, Command } from 'lucide-react';
// import Swiper and modules styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const CategorySelect = ({ ticketDetails, eventDetails }) => {
    // Function to format the date to 'August 3, 2023'
    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    // Function to safely format date with validation
    function formatEventDate(eventDetails) {
        if (!eventDetails || !eventDetails.start_date_event) {
            return 'Date not available';
        }

        try {
            const date = new Date(eventDetails.start_date_event);
            if (isNaN(date.getTime())) {
                return 'Invalid date';
            }
            return formatDate(date);
        } catch (error) {
            return 'Date not available';
        }
    }

    // Function to safely format end date with validation
    function formatEventEndDate(eventDetails) {
        if (!eventDetails || !eventDetails.end_date_event) {
            return 'Date not available';
        }

        try {
            const date = new Date(eventDetails.end_date_event);
            if (isNaN(date.getTime())) {
                return 'Invalid date';
            }
            return formatDate(date);
        } catch (error) {
            return 'Date not available';
        }
    }

    return (
        <div className="space-y-6">
            <div>
                <div className="space-y-1 mb-2 flex flex-col sm:flex-row gap-2 sm:gap-4">
                    <p className="text-orange-500 font-semibold text-sm md:text-base">
                        Event Start Date: {formatEventDate(eventDetails)}
                    </p>
                    <p className="text-gray-600 font-medium text-sm md:text-base">
                        End: {formatEventEndDate(eventDetails)}
                    </p>
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                    Select Your Ticket Type
                </h3>
            </div>

            <div className="space-y-4">
                {ticketDetails && ticketDetails.map(item => (
                    <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 md:gap-4">
                            <div className="flex-1">
                                <h4 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
                                    {item.ticket_category.name}
                                </h4>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600">
                                    <span className="text-lg md:text-2xl font-bold text-orange-500">
                                        Ugx {item.ticket_price.toLocaleString()}
                                    </span>
                                    <span className="bg-green-100 text-green-700 px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-medium">
                                        {item.tickets_available.toLocaleString()} Available
                                    </span>
                                </div>
                            </div>
                            <Link
                                to={`/ticket-purchase?event=${item.slug}&ticket=${item.id}`}
                                className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 !text-white px-4 py-2 md:px-6 md:py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 text-sm md:text-base"
                            >
                                Select Ticket
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    )
}

const EventDetails = () => {
    let { name } = useParams();
    const { loading, setLoading } = useLoadingContext()
    const [show, setShow] = useState(false);
    const { getRequest } = useApiRequest()
    const [eventDetails, setEventDetails] = useState(null)
    const [otherEvents, setOtherEvents] = useState(null)
    const [ticketDetails, setTicketDetails] = useState(null)
    const [cheapestTicket, setCheapestTicket] = useState(null);
    const [highestTicket, setHighestTicket] = useState(null);

    const filterTickets = (tickets) => {
        if (!tickets || tickets.length === 0) return;

        const cheapest = tickets.reduce((prev, curr) => {
            return (prev.ticket_price < curr.ticket_price) ? prev : curr;
        });

        const highest = tickets.reduce((prev, curr) => {
            return (prev.ticket_price > curr.ticket_price) ? prev : curr;
        });

        setCheapestTicket(cheapest);
        setHighestTicket(highest);
    };

    // Function to format the date to 'August 3, 2023'
    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    // Function to safely format date with validation
    function formatEventDate(dateString) {
        if (!dateString) {
            return 'Date not available';
        }

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Invalid date';
            }
            return formatDate(date);
        } catch (error) {
            return 'Date not available';
        }
    }

    const fetchEventDetails = async () => {
        const details = await getRequest(`${domain}/event-details/${name}`)
        setEventDetails(details.body.details)
        setOtherEvents(details.body.others)
    }

    const fetchTicketDetails = async () => {
        const details = await getRequest(`${domain}/get-tickets?slug=${name}`)
        setTicketDetails(details.body)
        filterTickets(details.body);
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            if (!name) {
                setLoading(false);
                return;
            }

            try {
                // Perform all fetch requests in parallel and wait for all of them to complete
                await Promise.all([fetchEventDetails(), fetchTicketDetails()]);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                // Set loading to false when all requests are completed (or error occurs)
                setLoading(false);
            }
        };

        fetchData();
    }, [name]);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const truncateString = (str) => {
        return str && str.length > 90 ? str.substring(0, 89) + "..." : str;
    }

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Function to handle scroll event
        const handleScroll = () => {
            // Check if the user has scrolled more than 100px from the top
            if (window.scrollY > 100) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        // Attach the event listener for scrolling
        window.addEventListener('scroll', handleScroll);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Generate shareable URLs
    const shareUrl = window.location.href;
    const shareTitle = eventDetails?.event_plot_name || 'Check out this event!';
    const shareDescription = truncateString(eventDetails?.event_description) || 'Join us for an exciting event!';
    const whatsappShare = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} - ${shareDescription} ${shareUrl}`)}`;
    const facebookShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle)}`;
    const twitterShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareTitle} - ${shareDescription}`)}&url=${encodeURIComponent(shareUrl)}`;
    const instagramMessage = "To share on Instagram, copy the event link and paste it in your Instagram post or story!";

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Header />

            {eventDetails && (
                <>
                    {/* Hero Section */}
                    <div className="relative">
                        <div className="relative h-64 md:h-96 overflow-hidden">
                            <img
                                className="w-full h-full object-cover"
                                src={domain + eventDetails.banner}
                                alt={eventDetails.event_plot_name}
                            />
                            <div className="absolute inset-0 bg-black/20"></div>

                            {/* Like Button */}
                            <div className="absolute top-4 right-4 text-gray-200">
                                <Likes slug={name} />
                            </div>

                            {/* Event Info Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent p-4 md:p-8">
                                <div className="container mx-auto">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="bg-orange-500 !text-white px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                            {eventDetails.event_category.name}
                                        </span>
                                    </div>
                                    <p className="text-2xl md:text-4xl font-bold text-gray-100 mb-2 hidden">
                                        {eventDetails.event_plot_name}
                                    </p>
                                    <p className="text-gray-200 text-sm md:text-lg hidden">
                                        By {eventDetails.organiser.organiser_name}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="container mx-auto px-4 py-6 md:py-8">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Left Column - Event Details */}
                            <div className="lg:col-span-2 space-y-6 md:space-y-8">
                                {/* About Section */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-2xl shadow-lg p-4 md:p-8"
                                >
                                    <div>
                                        <h2 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                                            <Info className="w-5 h-5 md:w-6 md:h-6 text-teal-500" />
                                            {eventDetails.event_plot_name}
                                        </h2>
                                        <p className="text-gray-400 mb-2 md:mb-3 leading-relaxed text-sm! border-b border-gray-100 pb-3">
                                            By {eventDetails.organiser.organiser_name}
                                        </p>
                                    </div>
                                    <p className="text-gray-700 leading-relaxed text-sm!">
                                        {eventDetails.event_description}
                                    </p>
                                </motion.div>

                                {/* Event Details */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                    className="bg-white rounded-2xl shadow-lg p-4 md:p-8"
                                >
                                    <h2 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2 mb-4 md:mb-6 border-b border-gray-100 pb-3">
                                        <Command className="w-5 h-5 md:w-6 md:h-6 text-teal-500" />
                                        Event Details
                                    </h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                                        {/* Date and Time */}
                                        <div className="space-y-3 md:space-y-4">
                                            <h3 className="text-base md:text-lg font-semibold text-gray-800 flex items-center gap-2">
                                                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-orange-500" />
                                                Date and Time
                                            </h3>
                                            <div className="space-y-2 text-sm md:text-base text-gray-600">
                                                <p><strong>Start:</strong> {formatEventDate(eventDetails.start_date_event)}</p>
                                                <p><strong>End:</strong> {formatEventDate(eventDetails.end_date_event)}</p>
                                            </div>
                                        </div>

                                        {/* Location */}
                                        <div className="space-y-3 md:space-y-4">
                                            <h3 className="text-base md:text-lg font-semibold text-gray-800 flex items-center gap-2">
                                                <MapPin className="w-4 h-4 md:w-5 md:h-5 text-teal-500" />
                                                Location
                                            </h3>
                                            <div className="space-y-2 text-sm md:text-base text-gray-600">
                                                <p><strong>Venue:</strong> {eventDetails.venue_name}</p>
                                                <p><strong>Address:</strong> {eventDetails.venue_address}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Details */}
                                    <div className="mt-2 md:mt-8 space-y-4 md:space-y-6">
                                        <div>
                                            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2"><UserCog className='text-teal-500' /> Age Restriction</h3>
                                            <p className="text-sm md:text-base text-gray-600">{eventDetails.age_restriction}</p>
                                        </div>

                                        <div>
                                            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2"><TicketSlash className='text-teal-500' /> Refund Policy</h3>
                                            <p className="text-sm md:text-base text-gray-600">{eventDetails.refund_policy}</p>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Map */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="bg-white rounded-2xl shadow-lg overflow-hidden"
                                >
                                    <div className="p-6 border-b border-gray-100">
                                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <MapPin className="w-5 h-5 text-teal-500" />
                                            Event Location
                                        </h3>
                                    </div>
                                    <iframe
                                        width="100%"
                                        height="300"
                                        frameBorder="0"
                                        style={{ border: 0 }}
                                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBGY-aKP5KQbjJbf2B2mN6u9909uI5iv50&q=${eventDetails.venue_address}`}
                                        allowFullScreen
                                    />
                                </motion.div>

                                {/* Share Section */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="bg-white rounded-2xl shadow-lg p-8"
                                >
                                    <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <Share2 className="w-5 h-5 text-orange-500" />
                                        Share This Event
                                    </h3>
                                    <div className="flex gap-4">
                                        <motion.a
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href={whatsappShare}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center !text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-whatsapp text-xl"></i>
                                        </motion.a>
                                        <motion.a
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href={facebookShare}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 !bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center !text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-facebook text-xl"></i>
                                        </motion.a>
                                        <motion.a
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href="#"
                                            onClick={() => alert(instagramMessage)}
                                            className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full flex items-center justify-center !text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-instagram text-xl"></i>
                                        </motion.a>
                                        <motion.a
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href={twitterShare}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-sky-500 hover:bg-sky-600 rounded-full flex items-center justify-center !text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-twitter text-xl"></i>
                                        </motion.a>
                                    </div>
                                </motion.div>
                            </div>

                            {/* Right Column - Ticket Info */}
                            <div className="lg:col-span-1">
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="sticky top-8"
                                >
                                    <div className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
                                        {/* Price Range */}
                                        <div className="text-center">
                                            <h3 className="text-lg font-semibold text-gray-700 mb-2">Tickets Available From</h3>
                                            <div className="text-3xl font-bold text-orange-500">
                                                Ugx {cheapestTicket && cheapestTicket.ticket_price.toLocaleString()}
                                            </div>
                                            {highestTicket && (
                                                <div className="text-sm text-gray-500 mt-1">
                                                    to Ugx {highestTicket.ticket_price.toLocaleString()}
                                                </div>
                                            )}
                                        </div>

                                        <hr className="border-gray-200" />

                                        {/* Event Info */}
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-3 text-gray-600">
                                                <Calendar className="w-5 h-5 text-orange-500" />
                                                <span>{formatEventDate(eventDetails.start_date_event)}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-gray-600">
                                                <MapPin className="w-5 h-5 text-teal-500" />
                                                <span>{eventDetails.venue_name}</span>
                                            </div>
                                        </div>

                                        {/* Purchase Button */}
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleShow}
                                            className="w-full bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 !text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 mb-4"
                                        >
                                            <Ticket className="w-5 h-5" />
                                            Purchase Tickets
                                        </motion.button>

                                        {/* Event Image */}
                                        <div className="rounded-xl overflow-hidden">
                                            <img
                                                className="w-full h-48 object-cover"
                                                src={domain + eventDetails.listing_img}
                                                alt={eventDetails.event_plot_name}
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Other Events Section */}
                    <div className="container mx-auto px-4 py-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-8"
                        >
                            <h2 className="text-3xl font-bold text-gray-800 mb-2">
                                More Events in {eventDetails.event_category.name}
                            </h2>
                            <p className="text-gray-600">
                                Discover other amazing events in this category
                            </p>
                        </motion.div>

                        {otherEvents && otherEvents.length > 0 ? (
                            <Swiper
                                modules={[Navigation, Pagination, Autoplay]}
                                spaceBetween={30}
                                navigation
                                pagination={{ clickable: true }}
                                loop={true}
                                autoplay={{
                                    delay: 3000,
                                    disableOnInteraction: false,
                                }}
                                breakpoints={{
                                    640: {
                                        slidesPerView: 1,
                                        spaceBetween: 20,
                                    },
                                    768: {
                                        slidesPerView: 2,
                                        spaceBetween: 30,
                                    },
                                    1024: {
                                        slidesPerView: 3,
                                        spaceBetween: 30,
                                    },
                                }}
                                className="pb-12"
                            >
                                {otherEvents.map((item) => (
                                    <SwiperSlide key={item.id}>
                                        <EventCard event={item} />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        ) : (
                            <p className="text-center text-gray-600">No other events found in this category.</p>
                        )}
                    </div>
                </>
            )}

            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Purchase Tickets for {eventDetails?.event_plot_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CategorySelect ticketDetails={ticketDetails} eventDetails={eventDetails} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <Footer />
        </div>
    )
}

export default EventDetails;